import React from 'react';
import useSWR from 'swr';
import useOwnerOrgId from '@/lib/hooks/useOwnerOrgId';
import fetcher from '@/lib/swr/fetcher';
import type { BulkImport } from '@/modules/Settings';
import joinNonEmptyParams from '@/utils/joinNonEmptyParams';

type GetBulkImportResponse = {
  bulkImports: BulkImport[];
  bulkImport: BulkImport;
};

export default function useBulkImports(bulkImportId?: string): {
  data?: BulkImport[];
  error?: Error;
  isValidating: boolean;
} {
  const ownerOrgId = useOwnerOrgId();

  const key = React.useMemo(() => {
    if (!ownerOrgId) return null;

    const apiPath = bulkImportId
      ? `bulk-imports/${bulkImportId}`
      : 'bulk-imports';

    return `${apiPath}?${joinNonEmptyParams({
      ownerOrgId,
      limit: 1000,
    })}`;
  }, [bulkImportId, ownerOrgId]);

  const { data, error, isValidating } = useSWR(key, async (path) => {
    const res = (await fetcher(path)) as GetBulkImportResponse;
    return res?.bulkImports || [res?.bulkImport];
  });

  return {
    data,
    error,
    isValidating,
  };
}
